import { motion } from 'framer-motion';
import {
    Building,
    Camera,
    Eye,
    EyeOff,
    Globe,
    Lock,
    Mail,
    MapPin,
    Phone,
    Save,
    User
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const Profile = () => {
  const { recruiter, updateRecruiter, uploadLogo, changePassword } = useAuth();
  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [companyData, setCompanyData] = useState({
    companyName: '',
    companyType: '',
    companySize: '',
    industry: '',
    website: '',
    description: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    logo: ''
  });

  const [contactData, setContactData] = useState({
    firstName: '',
    lastName: '',
    jobTitle: '',
    email: '',
    phone: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  useEffect(() => {
    if (recruiter) {
      setCompanyData({
        companyName: recruiter.companyName || '',
        companyType: recruiter.companyType || '',
        companySize: recruiter.companySize || '',
        industry: recruiter.industry || '',
        website: recruiter.website || '',
        description: recruiter.description || '',
        address: recruiter.address || '',
        city: recruiter.city || '',
        state: recruiter.state || '',
        country: recruiter.country || '',
        postalCode: recruiter.postalCode || '',
        logo: recruiter.company?.logo || ''
      });

      setContactData({
        firstName: recruiter.contactPerson?.firstName || '',
        lastName: recruiter.contactPerson?.lastName || '',
        jobTitle: recruiter.contactPerson?.jobTitle || '',
        email: recruiter.email || '',
        phone: recruiter.phone || ''
      });
    }
  }, [recruiter]);

  const companyTypes = [
    'Startup',
    'Private Company',
    'Public Company',
    'Non-Profit',
    'Government',
    'Consulting',
    'Agency'
  ];

  const companySizes = [
    '1-10 employees',
    '11-50 employees',
    '51-200 employees',
    '201-500 employees',
    '501-1000 employees',
    '1000+ employees'
  ];

  const industries = [
    'Technology',
    'Healthcare',
    'Finance',
    'Education',
    'Retail',
    'Manufacturing',
    'Consulting',
    'Real Estate',
    'Marketing',
    'Other'
  ];

  const handleCompanyChange = (e) => {
    const { name, value } = e.target;
    setCompanyData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateCompanyData = () => {
    const newErrors = {};

    if (!companyData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!companyData.companyType) {
      newErrors.companyType = 'Company type is required';
    }

    if (!companyData.industry) {
      newErrors.industry = 'Industry is required';
    }

    if (companyData.website && !/^https?:\/\/.+/.test(companyData.website)) {
      newErrors.website = 'Please enter a valid URL';
    }

    if (!companyData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!companyData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!companyData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateContactData = () => {
    const newErrors = {};

    if (!contactData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!contactData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!contactData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(contactData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!contactData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePasswordData = () => {
    const newErrors = {};

    if (!passwordData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }

    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    
    if (!validateCompanyData()) return;

    setLoading(true);
    try {
      const result = await updateRecruiter(companyData);
      if (result.success) {
        toast.success('Company information updated successfully');
      }
    } catch (error) {
      console.error('Error updating company data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateContactData()) return;

    setLoading(true);
    try {
      const result = await updateRecruiter({
        contactPerson: {
          firstName: contactData.firstName,
          lastName: contactData.lastName,
          jobTitle: contactData.jobTitle
        },
        email: contactData.email,
        phone: contactData.phone
      });
      
      if (result.success) {
        toast.success('Contact information updated successfully');
      }
    } catch (error) {
      console.error('Error updating contact data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePasswordData()) return;

    setLoading(true);
    try {
      const result = await changePassword(passwordData.currentPassword, passwordData.newPassword);
      if (result.success) {
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        toast.success('Password changed successfully');
      }
    } catch (error) {
      console.error('Error changing password:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setLoading(true);
    try {
      const result = await uploadLogo(file);
      if (result.success) {
        setCompanyData(prev => ({
          ...prev,
          logo: result.data.logoUrl
        }));
        toast.success('Logo uploaded successfully, please refresh');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const tabs = [
    { id: 'company', name: 'Company Info', icon: Building },
    { id: 'contact', name: 'Contact Person', icon: User },
    { id: 'security', name: 'Security', icon: Lock }
  ];

  const getLogoUrl = (logo) => {
    let filename = '';
    if (logo && typeof logo === 'object' && logo.url) {
      filename = logo.url.split('/').pop();
    } else if (typeof logo === 'string') {
      filename = logo.split('/').pop();
    }
    if (!filename) return '';
    const url = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/upload/public/logos/${filename}`;
    return url;
  };

  const logoUrl = getLogoUrl(companyData.logo);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Company Profile</h1>
        <p className="text-gray-600">
          Manage your company information and account settings
        </p>
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="border-b border-gray-200 mb-8"
      >
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </motion.div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Company Information Tab */}
        {activeTab === 'company' && (
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Company Information</h2>
            
            <form onSubmit={handleCompanySubmit} className="space-y-6">
              {/* Logo Upload */}
              <div>
                <label className="form-label">Company Logo</label>
                <div className="flex items-center space-x-6">
                  <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt="Company Logo"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Building className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <label className="btn-secondary cursor-pointer flex items-center">
                      <Camera className="w-4 h-4 mr-2" />
                      Upload Logo
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </label>
                    <p className="text-sm text-gray-500 mt-1">
                      PNG, JPG up to 5MB
                    </p>
                  </div>
                </div>
              </div>

              {/* Basic Company Info */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Company Name *</label>
                  <input
                    type="text"
                    name="companyName"
                    value={companyData.companyName}
                    onChange={handleCompanyChange}
                    className={`input-field ${errors.companyName ? 'border-red-300' : ''}`}
                    placeholder="Enter company name"
                  />
                  {errors.companyName && <p className="mt-2 text-sm text-red-600">{errors.companyName}</p>}
                </div>

                <div>
                  <label className="form-label">Company Type *</label>
                  <select
                    name="companyType"
                    value={companyData.companyType}
                    onChange={handleCompanyChange}
                    className={`input-field ${errors.companyType ? 'border-red-300' : ''}`}
                  >
                    <option value="">Select company type</option>
                    {companyTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {errors.companyType && <p className="mt-2 text-sm text-red-600">{errors.companyType}</p>}
                </div>

                <div>
                  <label className="form-label">Company Size</label>
                  <select
                    name="companySize"
                    value={companyData.companySize}
                    onChange={handleCompanyChange}
                    className="input-field"
                  >
                    <option value="">Select company size</option>
                    {companySizes.map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label">Industry *</label>
                  <select
                    name="industry"
                    value={companyData.industry}
                    onChange={handleCompanyChange}
                    className={`input-field ${errors.industry ? 'border-red-300' : ''}`}
                  >
                    <option value="">Select industry</option>
                    {industries.map(industry => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                  {errors.industry && <p className="mt-2 text-sm text-red-600">{errors.industry}</p>}
                </div>
              </div>

              {/* Website */}
              <div>
                <label className="form-label">Website</label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="url"
                    name="website"
                    value={companyData.website}
                    onChange={handleCompanyChange}
                    className={`input-field pl-10 ${errors.website ? 'border-red-300' : ''}`}
                    placeholder="https://www.company.com"
                  />
                </div>
                {errors.website && <p className="mt-2 text-sm text-red-600">{errors.website}</p>}
              </div>

              {/* Description */}
              <div>
                <label className="form-label">Company Description</label>
                <textarea
                  name="description"
                  value={companyData.description}
                  onChange={handleCompanyChange}
                  rows={4}
                  className="input-field"
                  placeholder="Brief description of your company"
                />
              </div>

              {/* Address */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Company Address</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="form-label">Address *</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <textarea
                        name="address"
                        value={companyData.address}
                        onChange={handleCompanyChange}
                        rows={3}
                        className={`input-field pl-10 ${errors.address ? 'border-red-300' : ''}`}
                        placeholder="Enter company address"
                      />
                    </div>
                    {errors.address && <p className="mt-2 text-sm text-red-600">{errors.address}</p>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">City *</label>
                      <input
                        type="text"
                        name="city"
                        value={companyData.city}
                        onChange={handleCompanyChange}
                        className={`input-field ${errors.city ? 'border-red-300' : ''}`}
                        placeholder="Enter city"
                      />
                      {errors.city && <p className="mt-2 text-sm text-red-600">{errors.city}</p>}
                    </div>

                    <div>
                      <label className="form-label">State/Province</label>
                      <input
                        type="text"
                        name="state"
                        value={companyData.state}
                        onChange={handleCompanyChange}
                        className="input-field"
                        placeholder="Enter state/province"
                      />
                    </div>

                    <div>
                      <label className="form-label">Country *</label>
                      <input
                        type="text"
                        name="country"
                        value={companyData.country}
                        onChange={handleCompanyChange}
                        className={`input-field ${errors.country ? 'border-red-300' : ''}`}
                        placeholder="Enter country"
                      />
                      {errors.country && <p className="mt-2 text-sm text-red-600">{errors.country}</p>}
                    </div>

                    <div>
                      <label className="form-label">Postal Code</label>
                      <input
                        type="text"
                        name="postalCode"
                        value={companyData.postalCode}
                        onChange={handleCompanyChange}
                        className="input-field"
                        placeholder="Enter postal code"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex items-center"
                >
                  {loading ? (
                    <div className="loading-spinner mr-2"></div>
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Contact Person Tab */}
        {activeTab === 'contact' && (
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Contact Person</h2>
            
            <form onSubmit={handleContactSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">First Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="firstName"
                      value={contactData.firstName}
                      onChange={handleContactChange}
                      className={`input-field pl-10 ${errors.firstName ? 'border-red-300' : ''}`}
                      placeholder="Enter first name"
                    />
                  </div>
                  {errors.firstName && <p className="mt-2 text-sm text-red-600">{errors.firstName}</p>}
                </div>

                <div>
                  <label className="form-label">Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={contactData.lastName}
                    onChange={handleContactChange}
                    className={`input-field ${errors.lastName ? 'border-red-300' : ''}`}
                    placeholder="Enter last name"
                  />
                  {errors.lastName && <p className="mt-2 text-sm text-red-600">{errors.lastName}</p>}
                </div>
              </div>

              <div>
                <label className="form-label">Job Title</label>
                <input
                  type="text"
                  name="jobTitle"
                  value={contactData.jobTitle}
                  onChange={handleContactChange}
                  className="input-field"
                  placeholder="e.g. HR Manager, Talent Acquisition"
                />
              </div>

              <div>
                <label className="form-label">Email Address *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={contactData.email}
                    onChange={handleContactChange}
                    className={`input-field pl-10 ${errors.email ? 'border-red-300' : ''}`}
                    placeholder="Enter email address"
                  />
                </div>
                {errors.email && <p className="mt-2 text-sm text-red-600">{errors.email}</p>}
              </div>

              <div>
                <label className="form-label">Phone Number *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={contactData.phone}
                    onChange={handleContactChange}
                    className={`input-field pl-10 ${errors.phone ? 'border-red-300' : ''}`}
                    placeholder="Enter phone number"
                  />
                </div>
                {errors.phone && <p className="mt-2 text-sm text-red-600">{errors.phone}</p>}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex items-center"
                >
                  {loading ? (
                    <div className="loading-spinner mr-2"></div>
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Change Password</h2>
            
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div>
                <label className="form-label">Current Password *</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    className={`input-field pl-10 pr-10 ${errors.currentPassword ? 'border-red-300' : ''}`}
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    {showPasswords.current ? (
                      <EyeOff className="w-5 h-5 text-gray-400" />
                    ) : (
                      <Eye className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.currentPassword && <p className="mt-2 text-sm text-red-600">{errors.currentPassword}</p>}
              </div>

              <div>
                <label className="form-label">New Password *</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className={`input-field pl-10 pr-10 ${errors.newPassword ? 'border-red-300' : ''}`}
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    {showPasswords.new ? (
                      <EyeOff className="w-5 h-5 text-gray-400" />
                    ) : (
                      <Eye className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.newPassword && <p className="mt-2 text-sm text-red-600">{errors.newPassword}</p>}
              </div>

              <div>
                <label className="form-label">Confirm New Password *</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className={`input-field pl-10 pr-10 ${errors.confirmPassword ? 'border-red-300' : ''}`}
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    {showPasswords.confirm ? (
                      <EyeOff className="w-5 h-5 text-gray-400" />
                    ) : (
                      <Eye className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && <p className="mt-2 text-sm text-red-600">{errors.confirmPassword}</p>}
              </div>

              {/* Password Requirements */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Password Requirements:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• At least 6 characters long</li>
                  <li>• Mix of uppercase and lowercase letters recommended</li>
                  <li>• Include numbers and special characters for better security</li>
                </ul>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex items-center"
                >
                  {loading ? (
                    <div className="loading-spinner mr-2"></div>
                  ) : (
                    <Lock className="w-4 h-4 mr-2" />
                  )}
                  Change Password
                </button>
              </div>
            </form>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Profile; 